import React from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  CircularProgress,
  TextField,
} from "@mui/material";
import {
  Close,
  Description,
  Business,
  CurrencyExchange,
  Visibility,
  Receipt,
} from "@mui/icons-material";
import { useTheme } from "../../ThemeContext";

const InvoiceDialog = ({
  open,
  onClose,
  invoiceChoice,
  setInvoiceChoice,
  selectedCompany,
  setSelectedCompany,
  allowedCompanies = ["UK", "UAE"],
  useManualAED,
  setUseManualAED,
  showGBP,
  setShowGBP,
  exchangeRateSource,
  setExchangeRateSource,
  exchangeRateAPIRate,
  cbuaeRate,
  loadingRates,
  isGeneratingInvoice,
  onCreateInvoice,
  isMultiProject = false,
  combinedInvoiceNo = "",
  setCombinedInvoiceNo,
}) => {
  const { theme } = useTheme();
  const isCompanySelectorLocked = allowedCompanies.length === 1;
  const canSelectUK = allowedCompanies.includes("UK");
  const canSelectUAE = allowedCompanies.includes("UAE");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <div
        className={`${theme.formHeaderBg} px-6 py-4 flex items-center justify-between`}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm">
            <Receipt className="text-white" />
          </div>
          <div>
            <Typography variant="h6" className="text-white font-bold">
              Generate Invoice
            </Typography>
            <Typography variant="caption" className={`${theme.formSubText}`}>
              Configure your invoice settings
            </Typography>
          </div>
        </div>
        <IconButton
          onClick={onClose}
          sx={{ color: "white" }}
          className="hover:bg-white/10"
        >
          <Close />
        </IconButton>
      </div>

      <DialogContent sx={{ p: 3 }}>
        <div className="space-y-6">
          {isMultiProject && typeof setCombinedInvoiceNo === "function" && (
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
              <div className="flex items-center gap-2 mb-3">
                <Receipt className="text-gray-500 w-5 h-5" />
                <Typography
                  variant="subtitle2"
                  className="font-bold text-gray-700"
                >
                  Combined Invoice Number
                </Typography>
              </div>
              <TextField
                fullWidth
                size="small"
                value={combinedInvoiceNo}
                onChange={(e) => setCombinedInvoiceNo(e.target.value)}
                placeholder="Optional override (e.g., INVABC2025)"
                helperText="Leave blank to auto-generate"
                InputProps={{ sx: { backgroundColor: "white" } }}
              />
            </div>
          )}

          {/* Subject Visibility Section */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <Visibility className="text-gray-500 w-5 h-5" />
              <Typography
                variant="subtitle2"
                className="font-bold text-gray-700"
              >
                Subject Visibility
              </Typography>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setInvoiceChoice("Show subject")}
                className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-2 ${
                  invoiceChoice === "Show subject"
                    ? "border-slate-500 bg-white shadow-sm"
                    : "border-transparent bg-gray-100 hover:bg-gray-200"
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    invoiceChoice === "Show subject"
                      ? "border-slate-500"
                      : "border-gray-400"
                  }`}
                >
                  {invoiceChoice === "Show subject" && (
                    <div className="w-2 h-2 rounded-full bg-slate-500" />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Show Subject
                </span>
              </button>

              <button
                onClick={() => setInvoiceChoice("Anonymise subject")}
                className={`p-3 rounded-lg border-2 text-left transition-all flex items-center gap-2 ${
                  invoiceChoice === "Anonymise subject"
                    ? "border-slate-500 bg-white shadow-sm"
                    : "border-transparent bg-gray-100 hover:bg-gray-200"
                }`}
              >
                <div
                  className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                    invoiceChoice === "Anonymise subject"
                      ? "border-slate-500"
                      : "border-gray-400"
                  }`}
                >
                  {invoiceChoice === "Anonymise subject" && (
                    <div className="w-2 h-2 rounded-full bg-slate-500" />
                  )}
                </div>
                <span className="text-sm font-medium text-gray-700">
                  Anonymise
                </span>
              </button>
            </div>
          </div>

          {/* Company Selection */}
          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            <div className="flex items-center gap-2 mb-3">
              <Business className="text-gray-500 w-5 h-5" />
              <Typography
                variant="subtitle2"
                className="font-bold text-gray-700"
              >
                Issuing Company
              </Typography>
            </div>
            <Typography variant="caption" className="block mb-3 text-gray-500">
              {isCompanySelectorLocked
                ? "Billing entity is locked to the project. Update the project company to change it."
                : "This project is unassigned, so either billing entity can be used."}
            </Typography>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSelectedCompany("UK")}
                disabled={isCompanySelectorLocked || !canSelectUK}
                className={`p-3 rounded-lg border-2 text-left transition-all flex flex-col gap-1 ${
                  selectedCompany === "UK"
                    ? "border-slate-500 bg-white shadow-sm"
                    : "border-transparent bg-gray-100"
                } ${
                  isCompanySelectorLocked || !canSelectUK
                    ? "cursor-not-allowed opacity-60"
                    : "hover:bg-gray-200"
                }`}
              >
                <span className="text-sm font-bold text-gray-800">
                  UK Company
                </span>
                <span className="text-xs text-gray-500">Fort Corps Ltd</span>
              </button>

              <button
                type="button"
                onClick={() => setSelectedCompany("UAE")}
                disabled={isCompanySelectorLocked || !canSelectUAE}
                className={`p-3 rounded-lg border-2 text-left transition-all flex flex-col gap-1 ${
                  selectedCompany === "UAE"
                    ? "border-slate-500 bg-white shadow-sm"
                    : "border-transparent bg-gray-100"
                } ${
                  isCompanySelectorLocked || !canSelectUAE
                    ? "cursor-not-allowed opacity-60"
                    : "hover:bg-gray-200"
                }`}
              >
                <span className="text-sm font-bold text-gray-800">
                  UAE Company
                </span>
                <span className="text-xs text-gray-500">Fort Corps FZ-LLC</span>
              </button>
            </div>
          </div>

          {/* UAE Specific Options */}
          {selectedCompany === "UAE" && (
            <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <div className="flex items-center gap-2 mb-3">
                  <CurrencyExchange className="text-slate-500 w-5 h-5" />
                  <Typography
                    variant="subtitle2"
                    className="font-bold text-slate-700"
                  >
                    Currency & Exchange Rates
                  </Typography>
                </div>

                <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-slate-300 transition-colors mb-3">
                  <input
                    type="checkbox"
                    checked={useManualAED}
                    onChange={(e) => setUseManualAED(e.target.checked)}
                    className="w-4 h-4 text-slate-600 rounded border-gray-300 focus:ring-slate-500"
                  />
                  <span className="text-sm text-gray-700">
                    Use manual AED values
                  </span>
                </label>

                {!useManualAED && (
                  <div className="ml-1 pl-4 border-l-2 border-slate-200 space-y-3 mb-3">
                    <Typography
                      variant="caption"
                      className="text-slate-500 font-medium uppercase tracking-wider"
                    >
                      Exchange Rate Source
                    </Typography>

                    {loadingRates ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <CircularProgress size={16} /> Loading rates...
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <button
                          onClick={() =>
                            setExchangeRateSource("ExchangeRate-API")
                          }
                          className={`w-full p-2 rounded-lg text-left text-sm flex items-center justify-between ${
                            exchangeRateSource === "ExchangeRate-API"
                              ? "bg-slate-100 text-slate-800 font-medium"
                              : "text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          <span>ExchangeRate-API</span>
                          {exchangeRateAPIRate && (
                            <span className="text-xs bg-white px-2 py-1 rounded border border-gray-200">
                              1 GBP = {exchangeRateAPIRate.toFixed(5)} AED
                            </span>
                          )}
                        </button>

                        <button
                          onClick={() => setExchangeRateSource("CBUAE")}
                          className={`w-full p-2 rounded-lg text-left text-sm flex items-center justify-between ${
                            exchangeRateSource === "CBUAE"
                              ? "bg-slate-100 text-slate-800 font-medium"
                              : "text-gray-600 hover:bg-gray-50"
                          }`}
                        >
                          <span>Central Bank of UAE</span>
                          {cbuaeRate && (
                            <span className="text-xs bg-white px-2 py-1 rounded border border-gray-200">
                              1 GBP = {cbuaeRate.toFixed(5)} AED
                            </span>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <label className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 cursor-pointer hover:border-slate-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={showGBP}
                    onChange={(e) => setShowGBP(e.target.checked)}
                    className="w-4 h-4 text-slate-600 rounded border-gray-300 focus:ring-slate-500"
                  />
                  <span className="text-sm text-gray-700">
                    Show GBP column on invoice
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
      </DialogContent>

      <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onCreateInvoice}
          disabled={isGeneratingInvoice}
          className={`px-6 py-2 text-sm font-medium text-white rounded-lg shadow-sm flex items-center gap-2 transition-all ${
            isGeneratingInvoice
              ? "bg-slate-400 cursor-not-allowed"
              : `${theme.toolbarAddButton}`
          }`}
        >
          {isGeneratingInvoice ? (
            <>
              <CircularProgress size={16} color="inherit" />
              Generating...
            </>
          ) : (
            <>
              <Description className="w-4 h-4" />
              Create Invoice
            </>
          )}
        </button>
      </div>
    </Dialog>
  );
};

export default InvoiceDialog;
